import { apiFetch } from '../lib/api';

export type DashboardRole = 'admin' | 'viewer' | 'session_viewer' | 'insights';

export const DASHBOARD_ROLES: DashboardRole[] = ['admin', 'viewer', 'session_viewer', 'insights'];

export const ROLE_LABELS: Record<DashboardRole, string> = {
    admin: 'Admin',
    viewer: 'Viewer',
    session_viewer: 'Session viewer',
    insights: 'Insights (read-only)',
};

export const ROLE_DESCRIPTIONS: Record<DashboardRole, string> = {
    admin: 'Full access to everything, including this Team page.',
    // Legacy role, kept only so existing accounts still render. It grants nothing:
    // every page requires admin, and Sessions/Reports reject it. Don't hand it out.
    viewer: 'Legacy — grants no access to any page. Do not use for new accounts.',
    session_viewer: 'Sessions & Reports only — used for municipality (BPM) accounts.',
    insights: 'Read-only analytics, events, feedback, heatmap, sessions & reports for advisors, investors and interns. No personal data (no plates, user/device IDs or raw events) and no changes.',
};

export interface DashboardUser {
    id: string;
    username: string;
    email: string;
    role: DashboardRole;
    isActive: boolean;
    mustChangePassword: boolean;
    lastLogin: string | null;
    allowedCities: string[];
    allowedAreaNos: string[];
    municipality: string | null;
    createdAt: string;
}

export interface CreateUserInput {
    username: string;
    email: string;
    role: DashboardRole;
    allowedCities?: string[];
}

export interface TempPasswordResult {
    user: DashboardUser;
    tempPassword: string;
}

/** apiFetch throws with the raw response body (JSON string) on error — unwrap the message. */
export function parseApiError(err: unknown): string {
    const msg = err instanceof Error ? err.message : String(err);
    try {
        const parsed = JSON.parse(msg);
        return parsed.error || msg;
    } catch {
        return msg;
    }
}

export async function listDashboardUsers(): Promise<DashboardUser[]> {
    const data = await apiFetch<{ users: DashboardUser[] }>('/api/admin/users');
    return data.users;
}

export async function createDashboardUser(input: CreateUserInput): Promise<TempPasswordResult> {
    return apiFetch<TempPasswordResult>('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(input),
    });
}

export type UpdateUserPatch = Partial<
    Pick<DashboardUser, 'role' | 'isActive' | 'allowedCities' | 'allowedAreaNos' | 'municipality'>
>;

export async function updateDashboardUser(id: string, patch: UpdateUserPatch): Promise<DashboardUser> {
    const data = await apiFetch<{ user: DashboardUser }>(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
    return data.user;
}

export async function resetDashboardUserPassword(id: string): Promise<TempPasswordResult> {
    return apiFetch<TempPasswordResult>(`/api/admin/users/${id}/reset-password`, {
        method: 'POST',
    });
}

export async function deleteDashboardUser(id: string): Promise<void> {
    await apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
}
